import React, { useState } from 'react';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../services/api';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';

const Field = ({ id, label, icon: Icon, error, children }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="relative mt-1">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
      {children}
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const RegisterPage = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '', acceptTerms: false, newsletter: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  usePageMeta({ title: t('auth.createAccount'), description: t('auth.registerSub'), noindex: true });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = t('auth.firstNameRequired');
    if (!formData.lastName) newErrors.lastName = t('auth.lastNameRequired');
    if (!formData.email) newErrors.email = t('auth.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('auth.emailInvalid');
    if (!formData.password) newErrors.password = t('auth.passwordRequired');
    else if (formData.password.length < 8) newErrors.password = t('auth.min8');
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t('auth.passwordsMismatch');
    if (!formData.acceptTerms) newErrors.acceptTerms = t('auth.acceptRequired');
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setIsLoading(true);
    setApiError('');
    try {
      await authAPI.register({ email: formData.email, password: formData.password, first_name: formData.firstName, last_name: formData.lastName, phone: formData.phone || null, newsletter: formData.newsletter });
      await authAPI.login({ email: formData.email, password: formData.password, remember_me: true });
      const redirectTo = new URLSearchParams(window.location.search).get('redirect');
      navigate(redirectTo && redirectTo.startsWith('/') ? redirectTo : '/');
    } catch (error) {
      setApiError(error.response?.data?.detail || t('auth.registerError'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = (k, extra = '') => `pl-10 ${extra} ${errors[k] ? 'border-red-500' : ''}`;
  const eye = <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 inline-flex items-center justify-center w-9 h-9" aria-label={t('auth.showPassword')}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>;

  return (
    <div className="relative min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
      <LanguageSwitcher variant="auth" />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block"><Logo variant="light" size="lg" className="mx-auto" /></Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-900" data-testid="register-title">{t('auth.createAccount')}</h1>
          <p className="mt-2 text-sm text-gray-600">{t('auth.registerSub')}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          {apiError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm" data-testid="register-error">{apiError}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field id="firstName" label={t('auth.firstName')} icon={User} error={errors.firstName}>
                <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={inputCls('firstName')} placeholder={t('auth.firstNamePh')} data-testid="register-first-name" />
              </Field>
              <Field id="lastName" label={t('auth.lastName')} icon={User} error={errors.lastName}>
                <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={inputCls('lastName')} placeholder={t('auth.lastNamePh')} data-testid="register-last-name" />
              </Field>
            </div>
            <Field id="email" label={t('auth.email')} icon={Mail} error={errors.email}>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className={inputCls('email')} placeholder={t('auth.emailPh2')} data-testid="register-email" />
            </Field>
            <Field id="phone" label={t('auth.phone')} icon={Phone}>
              <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="pl-10" placeholder={t('auth.phonePh')} data-testid="register-phone" />
            </Field>
            <Field id="password" label={t('auth.password')} icon={Lock} error={errors.password}>
              <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} className={inputCls('password', 'pr-10')} placeholder={t('auth.min8')} data-testid="register-password" />
              {eye}
            </Field>
            <Field id="confirmPassword" label={t('auth.confirmPassword')} icon={Lock} error={errors.confirmPassword}>
              <Input id="confirmPassword" name="confirmPassword" type={showPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} className={inputCls('confirmPassword')} placeholder={t('auth.repeatPh')} data-testid="register-confirm-password" />
            </Field>

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} className="mt-1 rounded border-gray-300 text-orange-500 focus:ring-orange-500" data-testid="register-accept-terms" />
                <span className="text-sm text-gray-600">
                  {t('auth.acceptPrefix')} <Link to="/page/conditions-generales" className="text-blue-600 hover:underline inline-block py-1.5">{t('auth.terms')}</Link> {t('auth.and')} <Link to="/page/protection-des-donnees" className="text-blue-600 hover:underline inline-block py-1.5">{t('auth.privacy')}</Link>
                </span>
              </label>
              {errors.acceptTerms && <p className="text-red-500 text-xs">{errors.acceptTerms}</p>}

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" name="newsletter" checked={formData.newsletter} onChange={handleChange} className="mt-1 rounded border-gray-300 text-orange-500 focus:ring-orange-500" data-testid="register-newsletter" />
                <span className="text-sm text-gray-600">{t('auth.newsletter')}</span>
              </label>
              <p className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="register-session-hint">{t('auth.sessionHint')}</p>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 mt-6" disabled={isLoading} data-testid="register-submit">
              {isLoading ? t('auth.creating') : t('auth.createMine')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('auth.haveAccount')}{' '}
              <Link to="/connexion" className="text-blue-600 hover:underline font-medium inline-block py-2" data-testid="register-login-link">{t('auth.login')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
